import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from "react";
import { apiRequest } from "../api";
import { useDatadog } from "./use-datadog";

interface SpendingLimitContextType {
  isLimitExceeded: boolean;
  currentSpend: number;
  limit: number;
  remaining: number;
  checkSpendingLimit: () => Promise<void>;
  showModal: boolean;
  closeModal: () => void;
}

const SpendingLimitContext = createContext<
  SpendingLimitContextType | undefined
>(undefined);

interface SpendingLimitProviderProps {
  children: ReactNode;
}

export const SpendingLimitProvider = ({
  children
}: SpendingLimitProviderProps) => {
  const { trackError } = useDatadog();
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [currentSpend, setCurrentSpend] = useState(0);
  const [limit, setLimit] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const fetchLimit = async () => {
    try {
      const data = await apiRequest<{
        limit: number;
      }>({
        subRoute: "user",
        path: "/get_spending_limit",
        method: "GET"
      });

      setLimit(data.limit);
      setRemaining(data.limit);
    } catch (error) {
      trackError("Error fetching spending limit", error);
    }
  };

  const checkSpendingLimit = async () => {
    try {
      const data = await apiRequest<{
        current_spend: number;
        limit: number;
        remaining: number;
        exceeded: boolean;
      }>({
        subRoute: "user",
        path: "/check_spending_limit",
        method: "GET"
      });

      setCurrentSpend(data.current_spend);
      setLimit(data.limit);
      setRemaining(data.remaining);
      setIsLimitExceeded(data.exceeded);

      if (data.exceeded && !showModal) {
        setShowModal(true);
      }
    } catch (error) {
      trackError("Error checking spending limit", error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Fetch limit on mount and listen for API events
  useEffect(() => {
    // Fetch the limit configuration on mount
    fetchLimit();

    // Listen for spending limit exceeded events from API calls
    const handleSpendingLimitExceeded = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsLimitExceeded(true);
      setShowModal(true);

      // Parse error message if available
      if (customEvent.detail?.detail) {
        const message = customEvent.detail.detail;
        const spendMatch = message.match(/Current spend: \$(\d+\.?\d*)/);
        const limitMatch = message.match(/Limit: \$(\d+\.?\d*)/);

        if (spendMatch) setCurrentSpend(parseFloat(spendMatch[1]));
        if (limitMatch) setLimit(parseFloat(limitMatch[1]));
      }
    };

    window.addEventListener(
      "spending-limit-exceeded",
      handleSpendingLimitExceeded
    );

    return () => {
      window.removeEventListener(
        "spending-limit-exceeded",
        handleSpendingLimitExceeded
      );
    };
  }, []);

  return (
    <SpendingLimitContext.Provider
      value={{
        isLimitExceeded,
        currentSpend,
        limit,
        remaining,
        checkSpendingLimit,
        showModal,
        closeModal
      }}
    >
      {children}
    </SpendingLimitContext.Provider>
  );
};

export const useSpendingLimit = () => {
  const context = useContext(SpendingLimitContext);
  if (context === undefined) {
    throw new Error(
      "useSpendingLimit must be used within a SpendingLimitProvider"
    );
  }
  return context;
};
